import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Flex,
  useToast,
  Button,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  Badge,
  VStack,
  Progress,
} from '@chakra-ui/react';
import { FiArrowLeft, FiDownload, FiClock, FiUser, FiHeadphones, FiFileText } from 'react-icons/fi';
import ChatMessageList from '../../components/chat/ChatMessageList';
import ScriptEditModal from '../../components/chat/ScriptEditModal';
import { useTenant } from '../../contexts/TenantContext';
import { formatCallTime, useCallTimezone } from '../../utils/callTimezone';
import CallTimezoneSelect from '../../components/CallTimezoneSelect';
import { LogsAPI, getTenantUUID } from '../../services/api';
import PageHelp from '../../components/PageHelp';
import { coalesceSameRoleTurns, getLiveTurnMessages, getPostCallTranscriptMessages } from './callDetailTranscript';
import { getCallEvidenceState } from './callDetailEvidence';
import { useLanguage } from '../../i18n';
import { callEndReasonLabel } from '../../utils/callEndReason';
import { getPortalPageCopy } from '../../portalPageCopy';
import CorrectionPanel from './corrections/CorrectionPanel';
import CallOutcomeCard from './CallOutcomeCard';
import { callRefs, mergeTicketContacts } from './serviceRecords';

// callbackPath: where 本通结果 links callbacks (null: show the number only).
// contactLookup(callRefs): ticket-center tickets for the call (ecommerce).
const CallDetailPage = ({
  portalBasePath = '/app',
  showFollowupLink = true,
  callbackPath = `${portalBasePath}/callback-requests`,
  contactLookup = null,
}) => {
  const { callId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const copy = getPortalPageCopy(lang, 'callDetail');
  const tenantInfo = useTenant();
  const [displayTimezone, setDisplayTimezone] = useCallTimezone();
  const toast = useToast();

  const [call, setCall] = useState(location.state?.call || null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScriptModalOpen, setIsScriptModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [recording, setRecording] = useState({ loading: false, url: '', error: '' });
  const [transcribing, setTranscribing] = useState(false);
  const [showToolMessages, setShowToolMessages] = useState(false);
  const [showLiveTurns, setShowLiveTurns] = useState(false);

  useEffect(() => {
    fetchCallDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  // Ticket-center tickets come from another service; the list may already have them.
  const callKey = call?.call_id;
  useEffect(() => {
    if (!contactLookup || !callKey || call?.ticket_numbers) return undefined;
    let live = true;
    contactLookup(callRefs(call))
      .then((result) => {
        if (live) setCall((prev) => (prev?.call_id === callKey ? mergeTicketContacts([prev], result?.items || [])[0] : prev));
      })
      .catch(() => { /* the outcome card still shows texts, callbacks and transfers */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactLookup, callKey]);

  const fetchCallDetails = async () => {
    setLoading(true);
    try {
      const tenantUUID = tenantInfo.getCurrentTenantInfo()?.uuid || getTenantUUID();
      const loadTranscript = async (baseCall) => {
        const msgs = baseCall.messages;
        if (msgs && msgs.length > 0) {
          setMessages(msgs);
          await loadRecording(baseCall, tenantUUID);
          return;
        }
        try {
          const full = await LogsAPI.fetchCallLogById(callId, tenantUUID);
          setCall((prev) => ({ ...prev, ...full }));
          setMessages(full.messages || []);
          await loadRecording(full, tenantUUID);
        } catch (e) {
          setMessages([]);
          await loadRecording(baseCall, tenantUUID);
        }
      };

      if (!call) {
        if (!tenantUUID) {
          toast({ title: copy.tenantRequired, description: copy.tenantRequiredDetail, status: 'warning', duration: 4000 });
          navigate(`${portalBasePath}/call-history`, { replace: true });
          setLoading(false);
          return;
        }
        try {
          const full = await LogsAPI.fetchCallLogById(callId, tenantUUID);
          setCall(full);
          setMessages(full.messages || []);
          await loadRecording(full, tenantUUID);
        } catch {
          toast({
            title: copy.callNotFound,
            status: 'error',
            duration: 3000,
          });
          navigate(`${portalBasePath}/call-history`);
        }
      } else {
        await loadTranscript(call);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast({
        title: copy.detailsLoadError,
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRecording = async (targetCall, tenantUUID) => {
    if (!targetCall?.call_id || !targetCall?.recording_available) {
      setRecording({ loading: false, url: '', error: '' });
      return;
    }
    setRecording({ loading: true, url: '', error: '' });
    try {
      const res = await LogsAPI.fetchRecordingUrl(targetCall.call_id, tenantUUID);
      setRecording({ loading: false, url: res.signed_url || '', error: '' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setRecording({ loading: false, url: '', error: detail || e.message || copy.recordingLoadFailed });
    }
  };

  const handleExport = () => {
    const element = document.createElement("a");
    const file = new Blob([JSON.stringify(call, null, 2)], {type: 'application/json'});
    element.href = URL.createObjectURL(file);
    element.download = `call-${callId}.json`;
    document.body.appendChild(element);
    element.click();
  };

  const handleEditScript = (message) => {
    setSelectedMessage(message);
    setIsScriptModalOpen(true);
  };

  const handleTranscribeRecording = async () => {
    if (!call?.call_id) return;
    setTranscribing(true);
    try {
      const tenantUUID = tenantInfo.getCurrentTenantInfo()?.uuid || getTenantUUID();
      const res = await LogsAPI.transcribeRecording(call.call_id, tenantUUID);
      setMessages(res.messages || []);
      setCall((prev) => ({ ...prev, messages: res.messages || [] }));
      toast({
        title: res.cached ? copy.transcriptLoaded : copy.transcriptGenerated,
        status: 'success',
        duration: 3000,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({
        title: copy.transcriptionFailed,
        description: detail || e.message || copy.transcriptionUnavailable,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setTranscribing(false);
    }
  };

  if (loading) {
    return <Flex justify="center" align="center" h="50vh"><Spinner size="xl" /></Flex>;
  }

  if (!call) return null;
  const postCallTranscriptMessages = getPostCallTranscriptMessages(messages);
  const liveTurnMessages = getLiveTurnMessages(messages);
  const hasTranscript = postCallTranscriptMessages.length > 0;
  const evidenceState = getCallEvidenceState({
    recordingAvailable: Boolean(call.recording_available),
    transcriptAvailable: hasTranscript,
  });
  const statusLabel = call.status === 'completed'
    ? copy.completed
    : call.status === 'failed'
      ? copy.failed
      : call.status;

  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column" overflow="hidden">
      <Box flex="1" minH={0} overflowY="auto" p={6}>
      <PageHelp
        title={copy.helpTitle}
        description={copy.helpDescription}
        steps={[
          { title: copy.reviewSummary, detail: copy.reviewSummaryDetail },
          { title: copy.listenRecording, detail: copy.listenRecordingDetail },
          { title: copy.generateTranscript, detail: copy.generateTranscriptDetail },
        ]}
        align="flex-end"
      />
      <Button leftIcon={<FiArrowLeft />} variant="ghost" mb={4} onClick={() => navigate(`${portalBasePath}/call-history`)}>
        {copy.back}
      </Button>

      <Flex justify="space-between" align="center" mb={6} gap={3} flexWrap="wrap">
        <VStack align="start" spacing={1} minW={0}>
          <Heading size="lg">{copy.pageTitle}</Heading>
          <Text color="gray.500" fontSize="sm" wordBreak="break-all">{copy.id}: {callId}</Text>
        </VStack>
        <Flex gap={2} flexWrap="wrap" align="center">
          {showFollowupLink && <Button as={Link} to={`${portalBasePath}/callback-requests?call_session_id=${encodeURIComponent(call?.session_id || callId)}`}>
            {lang === 'zh' ? '查看关联跟进' : 'View related follow-up'}
          </Button>}
          <CallTimezoneSelect value={displayTimezone} onChange={setDisplayTimezone} lang={lang} />
          <Button leftIcon={<FiDownload />} onClick={handleExport}>
            {copy.exportJson}
          </Button>
        </Flex>
      </Flex>

      <CallOutcomeCard call={call} lang={lang} callbackPath={callbackPath} timezone={displayTimezone} />

      <Card mb={6}>
        <CardHeader>
          <Heading size="md">{copy.summary}</Heading>
        </CardHeader>
        <CardBody>
          <Flex wrap="wrap" gap={10}>
            <VStack align="start">
              <Text color="gray.500" fontSize="sm"><FiUser style={{display:'inline'}}/> {copy.caller}</Text>
              <Text fontWeight="medium" fontSize="lg">{call.caller_number}</Text>
            </VStack>
            <VStack align="start">
              <Text color="gray.500" fontSize="sm"><FiClock style={{display:'inline'}}/> {copy.startTime}</Text>
              <Text fontWeight="medium">{formatCallTime(call.start_time, displayTimezone)}</Text>
            </VStack>
             <VStack align="start">
              <Text color="gray.500" fontSize="sm">{copy.duration}</Text>
              <Text fontWeight="medium">{call.duration?.toFixed(1)}s</Text>
            </VStack>
            <VStack align="start">
              <Text color="gray.500" fontSize="sm">{copy.status}</Text>
              <Badge colorScheme={call.status === 'completed' ? 'green' : 'red'}>{statusLabel}</Badge>
            </VStack>
            <VStack align="start">
              <Text color="gray.500" fontSize="sm">{copy.endReason}</Text>
              <Text title={call.end_reason || undefined}>{callEndReasonLabel(call.end_reason, lang)}</Text>
            </VStack>
          </Flex>
        </CardBody>
      </Card>

      <Card mb={6}>
          <CardHeader>
            <Flex align="center" gap={2}>
              <FiHeadphones />
              <Heading size="md">{copy.recording}</Heading>
            </Flex>
          </CardHeader>
          <CardBody>
            {!call.recording_available ? (
              <Text color="gray.600">{copy.noRecording}</Text>
            ) : recording.loading ? (
              <Flex align="center" gap={3}>
                <Spinner size="sm" />
                <Text color="gray.500">{copy.loadingRecording}</Text>
              </Flex>
            ) : recording.url ? (
              <Box>
                <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }} gap={4}>
                  <Box as="audio" controls src={recording.url} flex="1" minW={0} />
                  <Button
                    leftIcon={<FiFileText />}
                    colorScheme="blue"
                    onClick={handleTranscribeRecording}
                    isLoading={transcribing}
                    loadingText={copy.transcribing}
                  >
                    {copy.transcribe}
                  </Button>
                  <Button as="a" href={recording.url} download leftIcon={<FiDownload />} variant="outline">
                    {copy.download}
                  </Button>
                </Flex>
                {transcribing && (
                  <Box mt={4}>
                    <Progress size="sm" colorScheme="blue" isIndeterminate borderRadius="sm" />
                    <Text mt={2} fontSize="sm" color="gray.500">
                      {copy.transcribingDetail}
                    </Text>
                  </Box>
                )}
              </Box>
            ) : recording.error ? (
              <Text color="red.500">{recording.error}</Text>
            ) : (
              <Text color="orange.600">
                {copy.recordingMetadataOnly}
              </Text>
            )}
          </CardBody>
        </Card>

      {hasTranscript && (
      <Card>
        <CardHeader>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Heading size="md">{copy.transcript}</Heading>
            <Button
              size="sm"
              variant={showToolMessages ? 'solid' : 'outline'}
              colorScheme="gray"
              onClick={() => setShowToolMessages((v) => !v)}
            >
              {showToolMessages ? copy.hide : copy.show} {copy.toolMessages}
            </Button>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box maxH="50vh" minH="200px" overflowY="auto">
          {(() => {
            const structuredMessages = postCallTranscriptMessages.map((m) => ({
              ...m,
              role: m.role === 'agent' ? 'assistant' : m.role,
            }));
            let transcriptMessages = structuredMessages;
            if (!showToolMessages) {
              transcriptMessages = transcriptMessages.filter((m) => m.role !== 'tool');
            }
            transcriptMessages = coalesceSameRoleTurns(transcriptMessages);
            return (
              <ChatMessageList
                messages={transcriptMessages}
                onEditMessage={(nodeId, content, metadata) => handleEditScript({ nodeId, content, ...metadata })}
              />
            );
          })()}
          </Box>
        </CardBody>
      </Card>
      )}

      {!hasTranscript && (
        <Card>
          <CardHeader>
            <Heading size="md">{copy.transcript}</Heading>
          </CardHeader>
          <CardBody>
            <Text color="gray.600">
              {evidenceState === 'recording_only' && recording.url
                ? copy.noSavedTranscript
                : evidenceState === 'recording_only'
                  ? copy.recordingStatusFirst
                  : copy.noTranscript}
            </Text>
          </CardBody>
        </Card>
      )}

      {liveTurnMessages.length > 0 && (
        <Card mt={6}>
          <CardHeader>
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <Heading size="md">{lang === 'zh' ? '实时逐轮记录（含工具调用）' : 'Live turn-by-turn record (with tool calls)'}</Heading>
              <Button size="sm" variant="outline" onClick={() => setShowLiveTurns((value) => !value)}>
                {showLiveTurns ? (lang === 'zh' ? '收起' : 'Hide') : (lang === 'zh' ? '展开' : 'Show')}
              </Button>
            </Flex>
            <Text fontSize="sm" color="gray.500">
              {lang === 'zh'
                ? '通话过程中实时识别并按顺序保存，可能与录音转写略有不同；号码和邮箱已隐藏。'
                : 'Recognised live during the call and saved in order; may differ slightly from the recording transcript. Numbers and emails are hidden.'}
            </Text>
          </CardHeader>
          {showLiveTurns && (
            <CardBody>
              <Box maxH="50vh" overflowY="auto">
                <ChatMessageList messages={liveTurnMessages} />
              </Box>
            </CardBody>
          )}
        </Card>
      )}

      <CorrectionPanel
        recordRef={call.call_id}
        messages={liveTurnMessages.length ? liveTurnMessages : postCallTranscriptMessages}
        lang={lang}
      />

      {selectedMessage && (
        <ScriptEditModal
          isOpen={isScriptModalOpen}
          onClose={() => setIsScriptModalOpen(false)}
          message={selectedMessage}
        />
      )}
      </Box>
    </Box>
  );
};

export default CallDetailPage;
